import React, { FC, useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Card,
    Chip,
    Grid,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Stack,
    TextField,
    Typography,
    Checkbox,
    ListItemIcon,
    Collapse,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { Calendar } from "react-multi-date-picker";
import moment from "moment";
import "moment/locale/fr";
import frenchLocale from "data/locale";
import { useSelector } from "react-redux";
import { RootState } from "store";
import StayType from "models/StayType";
import SearchCriteria from "models/TransferModels/SearchCriteria";
import Country from "models/Country";
import classes from "./style";
import ReactCountryFlag from "react-country-flag";
import { getCountryISO2 } from "utils/flagUtils";
import { useNavigate, useLocation } from "react-router-dom";
import { toQueryParams } from "helpers/queryHelpers";
import { toggleByKey } from "utils/filters";
import ClickAwayListener from "@mui/base/ClickAwayListener";
import ASIE from '../../assets/images/continents/ASIE.png';
import OCEANIE from '../../assets/images/continents/OCEANIE.png';
import AMERIQUEDUSUD from '../../assets/images/continents/AMERIQUE DU SUD.png';
import EUROPE from '../../assets/images/continents/EUROPE.png';
import AFRIQUE from '../../assets/images/continents/AFRIQUE.png';
import AMERIQUEDUNORD from '../../assets/images/continents/AMERIQUE DU NORD.png';

moment.locale("fr");

const CONTINENTS: string[] = [
    "EUROPE",
    "ASIE",
    "AFRIQUE",
    "AMERIQUE DU NORD",
    "AMERIQUE DU SUD",
    "OCEANIE",
];

interface Props { searchCriteria?: SearchCriteria }

interface State {
    openedCalendar: boolean;
    openedDestinations: boolean;
    openedStayTypes: boolean;

    selectedDate: Date[];
    selectedDestinations: Country[];
    selectedContinents: string[];
    selectedStayTypes: StayType[];

    destinationSearch: string;
}

const initialState: State = {
    openedCalendar: false,
    openedDestinations: false,
    openedStayTypes: false,
    selectedDate: [],
    selectedDestinations: [],
    selectedContinents: [],
    selectedStayTypes: [],
    destinationSearch: "",
};

const normalizeToDateArray = (input: any): Date[] => {
    if (!input) return [];
    const arr = Array.isArray(input) ? input : [input];
    return arr
        .map((v) =>
            v && typeof v === "object" && typeof (v as any).toDate === "function"
                ? (v as any).toDate()
                : new Date(v),
        )
        .filter((d) => d instanceof Date && !Number.isNaN(d.getTime()));
};

const JeSaisOuJeVais: FC<Props> = ({ searchCriteria }) => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const { stayTypes, countries: destinations } = useSelector((s: RootState) => s.globalSlice);

    const [state, setState] = useState<State>(initialState);
    const weekDays = useMemo(() => ["D", "L", "M", "W", "J", "V", "S"], []);

    useEffect(() => {
        if (!searchCriteria) return;
        setState((prev) => ({
            ...prev,
            selectedDate: normalizeToDateArray([searchCriteria.from as any, searchCriteria.to as any]),
            selectedStayTypes: searchCriteria.stayTypes ?? [],
            selectedDestinations: searchCriteria.destinations ?? [],
            selectedContinents: (searchCriteria as any).continents ?? [],
        }));
    }, [searchCriteria]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") resetPanels();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, []);

    useEffect(() => {
        setState((p) => ({
            ...p,
            openedCalendar: false,
            openedDestinations: false,
            openedStayTypes: false,
        }));
    }, [isMobile]);

    const resetPanels = (overrides?: Partial<State>) =>
        setState((p) => ({
            ...p,
            openedCalendar: false,
            openedDestinations: false,
            openedStayTypes: false,
            ...(overrides ?? {}),
        }));

    const canSearch =
        (state.selectedDestinations.length > 0 || state.selectedContinents.length > 0) &&
        state.selectedDate.length === 2;

    const continentToImage = (c: string) => {
        switch (c) {
            case "AFRIQUE":
                return AFRIQUE;
            case "ASIE":
                return ASIE;
            case "AMERIQUE DU NORD":
                return AMERIQUEDUNORD;
            case "AMERIQUE DU SUD":
                return AMERIQUEDUSUD;
            case "AMERIQUE DU NORD":
                return EUROPE;
            default: return OCEANIE;
        }
    }

    const handleSubmit = () => {
        if (!canSearch) return;

        const query = toQueryParams({
            destinationsIds: state.selectedDestinations.map((d) => d.id),
            continents: state.selectedContinents,
            stayTypes: state.selectedStayTypes.map((k) => k.id),
            budgets: [],
            durations: [],
            transports: [],
            from: moment(new Date(state.selectedDate[0])).format("YYYY-MM-DD HH:mm:ss"),
            to: moment(new Date(state.selectedDate[1])).format("YYYY-MM-DD HH:mm:ss"),
        });
        navigate("/liste-profils?" + query);
    };

    const DesktopOpeners = (
        <Grid container sx={[classes.rechercheVoyage, pathname !== "/" && classes.bgGray]}>
            <Grid item xs={12} lg onClick={() => resetPanels({ openedCalendar: !state.openedCalendar })}>
                <Button
                    aria-expanded={state.openedCalendar}
                    aria-controls="panel-calendar"
                    sx={classes.fieldBtn}
                    onClick={(e) => {
                        e.stopPropagation();
                        resetPanels({ openedCalendar: !state.openedCalendar });
                    }}
                >
                    Quand :
                </Button>
                {state.selectedDate.length === 2 && (
                    <Typography variant="body1">
                        {`${moment(new Date(state.selectedDate[0])).format("ll")} > ${moment(
                            new Date(state.selectedDate[1]),
                        ).format("ll")}`}
                    </Typography>
                )}
            </Grid>
            <Grid item xs={12} lg onClick={() => resetPanels({ openedDestinations: !state.openedDestinations })}>
                <Button
                    aria-expanded={state.openedDestinations}
                    aria-controls="panel-destinations"
                    sx={classes.fieldBtn}
                    onClick={(e) => {
                        e.stopPropagation();
                        resetPanels({ openedDestinations: !state.openedDestinations });
                    }}
                >
                    Où :
                </Button>
                <Box>
                    {state.selectedContinents
                        .map((d) => <Chip key={d} label={d} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                        )}
                    {state.selectedDestinations
                        .map((d) => <Chip key={d.id} label={d.name} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                        )}
                </Box>
            </Grid>
            <Grid item xs={12} lg onClick={() => resetPanels({ openedStayTypes: !state.openedStayTypes })}>
                <Button
                    aria-expanded={state.openedStayTypes}
                    aria-controls="panel-staytypes"
                    sx={classes.fieldBtn}
                    onClick={(e) => {
                        e.stopPropagation();
                        resetPanels({ openedStayTypes: !state.openedStayTypes });
                    }}
                >
                    Ambiance :
                </Button>
                <Box>
                    {state.selectedStayTypes
                        .slice(0, 4)
                        .map((s) => <Chip key={s.id} label={s.name} size="small" sx={{ mr: 0.5, mb: 0.5 }} />)}
                </Box>
            </Grid>
            <Grid item xs={12} lg={1}>
                <Button sx={classes.fieldBtn} variant="contained" disabled={false && !canSearch} onClick={handleSubmit}>
                    <SearchOutlinedIcon />
                </Button>
            </Grid>
        </Grid>
    );

    const DesktopPanels = (
        <Grid container sx={[classes.rechercheVoyageFields]}>
            <Grid item xs={12} lg id="panel-calendar">
                {state.openedCalendar && (
                    <Calendar
                        locale={frenchLocale}
                        minDate={new Date()}
                        className={state.selectedDate.length > 1 ? "range" : "single"}
                        range
                        weekDays={weekDays}
                        weekStartDayIndex={1}
                        value={state.selectedDate as any}
                        onChange={(value: any) =>
                            setState((p) => ({ ...p, selectedDate: normalizeToDateArray(value) }))
                        }
                    />
                )}
            </Grid>

            <Grid item xs={12} lg id="panel-destinations">
                {state.openedDestinations && (
                    <>
                        <List style={{ maxHeight: 260, overflow: "auto" }} dense>
                            <ListItem disablePadding>
                                <TextField
                                    fullWidth
                                    autoFocus
                                    size="small"
                                    placeholder="Destination ..."
                                    variant="outlined"
                                    value={state.destinationSearch}
                                    onChange={(e) => setState((p) => ({ ...p, destinationSearch: e.target.value }))}
                                />
                            </ListItem>
                            {CONTINENTS
                                .filter((c) => c.toLowerCase().includes(state.destinationSearch.toLowerCase()))
                                .map((continent) => {
                                    const selected = state.selectedContinents.includes(continent);
                                    return (
                                        <ListItem key={continent} disablePadding>
                                            <ListItemButton
                                                role={undefined}
                                                onClick={() =>
                                                    setState((p) => ({
                                                        ...p,
                                                        selectedContinents: selected
                                                            ? p.selectedContinents.filter((x) => x !== continent)
                                                            : [...p.selectedContinents, continent],
                                                    }))
                                                }
                                                dense
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Stack direction="row" columnGap={1} alignItems="center">
                                                            {continent}
                                                            <img src={continentToImage(continent)} width={25} />
                                                        </Stack>
                                                    }
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    );
                                })}
                            {destinations
                                .filter((d) =>
                                    d.name.toLowerCase().includes(state.destinationSearch.toLowerCase()),
                                )
                                .map((destination) => (
                                    <ListItem key={destination.id} disablePadding>
                                        <ListItemButton
                                            role={undefined}
                                            onClick={() =>
                                                setState((p) => ({
                                                    ...p,
                                                    selectedDestinations: p.selectedDestinations.some(
                                                        (x) => x.id === destination.id,
                                                    )
                                                        ? p.selectedDestinations.filter((x) => x.id !== destination.id)
                                                        : [...p.selectedDestinations, destination],
                                                }))
                                            }
                                            dense
                                        >
                                            <ListItemText
                                                primary={
                                                    <Stack direction="row" columnGap={1} alignItems="center">
                                                        <ReactCountryFlag countryCode={getCountryISO2(destination.isoCode)} svg />
                                                        {destination.name}
                                                    </Stack>
                                                }
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                        </List>
                        <Box sx={{ mt: 1 }}>
                            {state.selectedContinents.map((c) => (
                                <Chip
                                    key={c}
                                    label={c}
                                    onDelete={() =>
                                        setState((p) => ({
                                            ...p,
                                            selectedContinents: p.selectedContinents.filter((x) => x !== c),
                                        }))
                                    }
                                    sx={{ mr: 0.5, mb: 0.5 }}
                                />
                            ))}
                            {state.selectedDestinations.map((d) => (
                                <Chip
                                    key={d.id}
                                    label={d.name}
                                    onDelete={() =>
                                        setState((p) => ({
                                            ...p,
                                            selectedDestinations: p.selectedDestinations.filter((x) => x.id !== d.id),
                                        }))
                                    }
                                    sx={{ mr: 0.5, mb: 0.5 }}
                                />
                            ))}
                        </Box>
                    </>
                )}
            </Grid>

            <Grid item xs={12} lg id="panel-staytypes">
                {state.openedStayTypes && (
                    <List dense>
                        {stayTypes.map((ambiance) => (
                            <ListItem key={ambiance.id} disablePadding>
                                <ListItemButton
                                    role={undefined}
                                    onClick={() =>
                                        setState((p) => ({
                                            ...p,
                                            selectedStayTypes: toggleByKey(ambiance, p.selectedStayTypes, "id"),
                                        }))
                                    }
                                    dense
                                >
                                    <ListItemText primary={ambiance.name} secondary={ambiance.description} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                )}
            </Grid>
        </Grid>
    );

    const MobileLayout = (
        <Grid container sx={classes.rechercheVoyage}>
            {/* Quand */}
            <Grid item xs={12} lg>
                <Button
                    sx={classes.fieldBtn}
                    onClick={() =>
                        setState((s) => ({
                            ...s,
                            openedCalendar: !s.openedCalendar,
                            openedDestinations: false,
                            openedStayTypes: false,
                        }))
                    }
                >
                    Quand :
                    {state.selectedDate.length === 2 && (
                        <Typography variant="body1" sx={{ ml: 1 }}>
                            {`${moment(new Date(state.selectedDate[0])).format("ll")} > ${moment(
                                new Date(state.selectedDate[1]),
                            ).format("ll")}`}
                        </Typography>
                    )}
                </Button>
                <Collapse in={state.openedCalendar}>
                    <Calendar
                        locale={frenchLocale}
                        minDate={new Date()}
                        className={state.selectedDate.length > 1 ? "range" : "single"}
                        range
                        weekDays={weekDays}
                        weekStartDayIndex={1}
                        value={state.selectedDate as any}
                        onChange={(value: any) => {
                            setState((p) => ({ ...p, selectedDate: normalizeToDateArray(value) }));
                        }}
                    />
                </Collapse>
            </Grid>

            <Grid item xs={12} lg>
                <Button
                    sx={classes.fieldBtn}
                    onClick={() =>
                        setState((s) => ({
                            ...s,
                            openedCalendar: false,
                            openedDestinations: !s.openedDestinations,
                            openedStayTypes: false,
                        }))
                    }
                >
                    Où :
                    {(state.selectedDestinations.length > 0 || state.selectedContinents.length > 0) && (
                        <Typography variant="body1" sx={{ ml: 1 }}>
                            {[...state.selectedContinents, ...state.selectedDestinations.map((e) => e.name)].join(", ")}
                        </Typography>
                    )}
                </Button>
                <Collapse in={state.openedDestinations}>
                    <List dense sx={{ maxHeight: 320, overflow: "auto" }}>
                        <ListItem disablePadding>
                            <TextField
                                fullWidth
                                autoFocus
                                size="small"
                                placeholder="Destination ..."
                                variant="outlined"
                                value={state.destinationSearch}
                                onChange={(e) => setState((p) => ({ ...p, destinationSearch: e.target.value }))}
                                sx={{ m: 1 }}
                            />
                        </ListItem>
                        {CONTINENTS
                            .filter((c) => c.toLowerCase().includes(state.destinationSearch.toLowerCase()))
                            .map((continent) => {
                                const checked = state.selectedContinents.includes(continent);
                                return (
                                    <ListItem key={continent} disablePadding>
                                        <ListItemButton
                                            dense
                                            onClick={() =>
                                                setState((p) => ({
                                                    ...p,
                                                    selectedContinents: checked
                                                        ? p.selectedContinents.filter((x) => x !== continent)
                                                        : [...p.selectedContinents, continent],
                                                }))
                                            }
                                        >
                                            <ListItemIcon>
                                                <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Stack direction="row" columnGap={1} alignItems="center">
                                                        {continent}
                                                        <img src={continentToImage(continent)} width={25} />
                                                    </Stack>
                                                }
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                        {destinations
                            .filter((d) =>
                                d.name.toLowerCase().includes(state.destinationSearch.toLowerCase()),
                            )
                            .map((destination) => {
                                const checked = state.selectedDestinations.some((x) => x.id === destination.id);
                                return (
                                    <ListItem key={destination.id} disablePadding>
                                        <ListItemButton
                                            dense
                                            onClick={() =>
                                                setState((p) => ({
                                                    ...p,
                                                    selectedDestinations: checked
                                                        ? p.selectedDestinations.filter((x) => x.id !== destination.id)
                                                        : [...p.selectedDestinations, destination],
                                                }))
                                            }
                                        >
                                            <ListItemIcon>
                                                <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Stack direction="row" columnGap={1} alignItems="center">
                                                        <ReactCountryFlag countryCode={getCountryISO2(destination.isoCode)} svg />
                                                        {destination.name}
                                                    </Stack>
                                                }
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                    </List>
                    {(state.selectedDestinations.length > 0 || state.selectedContinents.length > 0) && (
                        <Box sx={{ mt: 1 }}>
                            {state.selectedContinents.map((c) => (
                                <Chip
                                    key={c}
                                    label={c}
                                    onDelete={() =>
                                        setState((p) => ({
                                            ...p,
                                            selectedContinents: p.selectedContinents.filter((x) => x !== c),
                                        }))
                                    }
                                    sx={{ mr: 0.5, mb: 0.5 }}
                                />
                            ))}
                            {state.selectedDestinations.map((d) => (
                                <Chip
                                    key={d.id}
                                    label={d.name}
                                    onDelete={() =>
                                        setState((p) => ({
                                            ...p,
                                            selectedDestinations: p.selectedDestinations.filter((x) => x.id !== d.id),
                                        }))
                                    }
                                    sx={{ mr: 0.5, mb: 0.5 }}
                                />
                            ))}
                        </Box>
                    )}
                </Collapse>
            </Grid>

            <Grid item xs={12} lg>
                <Button
                    sx={classes.fieldBtn}
                    onClick={() =>
                        setState((s) => ({
                            ...s,
                            openedCalendar: false,
                            openedDestinations: false,
                            openedStayTypes: !s.openedStayTypes,
                        }))
                    }
                >
                    Ambiance :
                    {state.selectedStayTypes.length > 0 && (
                        <Typography variant="body1" sx={{ ml: 1 }}>
                            {state.selectedStayTypes.map((e) => e.name).join(", ")}
                        </Typography>
                    )}
                </Button>
                <Collapse in={state.openedStayTypes}>
                    <List dense>
                        {stayTypes.map((ambiance) => {
                            const checked = !!state.selectedStayTypes.find((e) => e.id === ambiance.id);
                            return (
                                <ListItem key={ambiance.id} disablePadding>
                                    <ListItemButton
                                        dense
                                        onClick={() => {
                                            setState((p) => ({
                                                ...p,
                                                selectedStayTypes: toggleByKey(ambiance, p.selectedStayTypes, "id"),
                                            }));
                                        }}
                                    >
                                        <ListItemIcon>
                                            <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                                        </ListItemIcon>
                                        <ListItemText primary={ambiance.name} secondary={ambiance.description} />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                </Collapse>
            </Grid>

            <Grid item xs={12} lg={1}>
                <Button variant="contained" disabled={!canSearch} onClick={handleSubmit}>
                    <SearchOutlinedIcon />
                </Button>
            </Grid>
        </Grid>
    );

    return (
        <Card sx={[classes.card, classes.fullWidth, isMobile ? {} : classes.inlineFields]}>
            <ClickAwayListener onClickAway={() => resetPanels()} mouseEvent="onMouseDown" touchEvent="onTouchStart">
                {isMobile ? (
                    MobileLayout
                ) : (
                    <Box>
                        {DesktopOpeners}
                        {DesktopPanels}
                    </Box>
                )}
            </ClickAwayListener>
        </Card>
    );
};

export default JeSaisOuJeVais;
